import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import feChangePaymentOption from 'actions/payment/feChangePaymentOption.action';
import MoneyInput from 'components/form/MoneyInput.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import CHECKOUT_OPTION from 'constants/checkoutOption.const';
import ELID from 'constants/elid.const';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCheckoutPaymentOption } from 'selectors/checkout.selectors';
import selectCalendarModes from 'selectors/selectCalendarModes.selector';
import fromAnyToCoinNum from 'utils/converters/any/fromAnyToCoinNum.util';
import fromAnyToCoinStr from 'utils/converters/any/fromAnyToCoinStr.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import replaceNaN from 'utils/values/replaceNaN.util';

import CN from './CheckoutFooter.module.scss';

const CTAG = 'CheckoutFooter';
const L = logger(CTAG); // eslint-disable-line no-unused-vars

// aliases for shorter code

const rebalance = ({ other, cash, checkoutOption, card, chargeCardOnFile, balance }) => {
  const parsedCash = replaceNaN(0, fromAnyToCoinNum(cash));
  const parsedOther = replaceNaN(0, fromAnyToCoinNum(other));

  const difference = balance - parsedCash - parsedOther;

  if (checkoutOption !== CHECKOUT_OPTION.terminal && card && chargeCardOnFile) {
    return {
      cardInStore: S.empty,
      cardOnFile: difference < 0 ? S.empty : difference,
      change: difference < 0 ? Math.abs(difference) : 0,
    };
  }

  if (cash === balance) {
    return { cardOnFile: S.empty, cardInStore: S.empty, change: 0 };
  }

  return difference >= 0 ? { cardOnFile: S.empty, cardInStore: difference, change: 0 } : { cardOnFile: S.empty, cardInStore: S.empty, change: Math.abs(difference) };
};

const CheckoutFooter = ({ balance, blockComplete, busyComplete, calendarModes, card, chargeCardOnFile, checkoutOption, onComplete, otherAmount, feChangePaymentOption }) => {
  const [cash, setCashValue] = useStateWhileMounted(S.zero);
  const [other, setOtherValue] = useStateWhileMounted(Number.parseFloat(otherAmount) > 0 ? otherAmount : S.empty);
  const { val: isBackShown, inv: isBackHidden, tip: toggleBack } = useTip(false);

  const { t } = useTranslation();

  const handleClickSlickPay = useCallback(() => void feChangePaymentOption(CHECKOUT_OPTION.slick), [feChangePaymentOption]);

  const handleClickPosTerminal = useCallback(() => void feChangePaymentOption(CHECKOUT_OPTION.terminal), [feChangePaymentOption]);

  const handleCashBlur = useCallback(() => void setCashValue(fromAnyToCoinStr(cash)), [cash, setCashValue]);
  const handleOtherBlur = useCallback(() => void setOtherValue(fromAnyToCoinStr(other)), [other, setOtherValue]);
  const handleCashChange = useCallback((ev) => void setCashValue(ev?.is?.valid ? ev?.raw : S.zero), [setCashValue]);

  const handleOtherChange = useCallback(
    //
    (ev) => {
      if (ev?.is?.valid) {
        setOtherValue(ev?.number > balance ? balance : ev?.raw);
      } else {
        setOtherValue(S.zero);
      }
    },
    [balance, setOtherValue],
  );

  const handleComplete = useCallback(
    () =>
      void call(`${CTAG}.onComplete`, onComplete, {
        checkoutOption,
        cash: fromAnyToCoinNum(cash),
        other: fromAnyToCoinNum(other) || 0,
      }),
    [cash, checkoutOption, onComplete, other],
  );

  useEffect(() => {
    // If user picks cash in the popup, this will trigger

    if (checkoutOption !== CHECKOUT_OPTION.cash) return;

    const parsedBalance = fromAnyToCoinStr(balance);
    if (!parsedBalance) return;

    L.debug('useEffect()', checkoutOption, balance);
    setCashValue(parsedBalance);
  }, [balance, checkoutOption, handleCashChange, setCashValue]);

  const rebalanceOptions = { other, cash, checkoutOption, card, chargeCardOnFile, balance };
  const { cardOnFile, cardInStore, change } = rebalance(rebalanceOptions);

  const currencySignClasses = cn({
    [CN.currencySign]: true,
    [CN.dark]: calendarModes.isDarkMode,
  });

  useMemo(() => void L.debug('()', { balance, cardOnFile, cardInStore, cash, other }, currencySignClasses), [balance, cardInStore, cardOnFile, cash, currencySignClasses, other]);

  return (
    <div
      data-bem="CheckoutFooter"
      className={cn({
        [CN.component]: true,
        [CN.dark]: calendarModes.isDarkMode,
      })}
    >
      <div data-bem="CheckoutFooter__balance" className={CN.balanceBar}>
        <span data-bem="CheckoutFooter__balance-to-pay" className={CN.balanceToPay}>
          <Space>{t('checkout.footer.balanceToPay')}</Space>
          <CurrencySign value={balance > 0 ? balance : 0} />
        </span>
      </div>

      {fromAnyToBool(change > 0) && (
        <div data-bem="CheckoutFooter__change" className={CN.changeBar}>
          <span data-bem="CheckoutFooter__change-to-give" className={CN.changeToGive}>
            <Space>{t('checkout.footer.changeToGive')} </Space>
            <CurrencySign value={change} />
          </span>
        </div>
      )}

      {/* Take payment button is visible on mobile */}
      {isBackShown ? (
        <div className={CN.mobileFooter} />
      ) : (
        <button onClick={toggleBack} className={CN.takePayment}>
          {t('checkout.footer.takePayment')}
        </button>
      )}

      <div
        data-bem="CheckoutFooter__flyout"
        className={cn({
          [CN.flyoutTable]: true,
          [CN.hide]: isBackHidden,
        })}
      >
        <div data-bem="CheckoutFooter__table" className={CN.flyoutTableBody}>
          <div className={CN.isMobile}>
            <div className={CN.toolbarRow}>
              <div className={CN.name}>{t('checkout.footer.paymentMethod')}</div>
              <div className={CN.amount}>{t('checkout.footer.amount')}</div>
            </div>
          </div>

          <div
            data-bem="CheckoutFooter__bottom-row"
            className={cn({
              [CN.flyoutTableRow]: true,
              [CN.withFourBoxes]: card,
            })}
          >
            <div className={CN.inputsContainer}>
              {card && (
                <div data-bem="CheckoutFooter__card-on-file" className={CN.boxFooter}>
                  <span className={CN.codeLabelFixed}>
                    {t('checkout.footer.cardEnding')} <span className={CN.card}>{card}</span>
                  </span>
                  <div className={currencySignClasses}>
                    <CurrencySign />
                  </div>
                  <div className={CN.input}>
                    <MoneyInput id={ELID.checkoutSlick} type="text" placeholder="" value={cardOnFile} onClick={handleClickSlickPay} readOnly />
                  </div>
                </div>
              )}

              <div data-bem="CheckoutFooter__card-in-store" className={CN.boxFooter}>
                <span className={CN.codeLabelFixedSm}>{t('checkout.footer.instoreCard')}</span>
                <div className={currencySignClasses}>
                  <CurrencySign />
                </div>
                <div className={CN.input}>
                  <MoneyInput id={ELID.checkoutTerminal} type="text" placeholder="" value={fromAnyToCoinStr(cardInStore)} onClick={handleClickPosTerminal} readOnly />
                </div>
              </div>

              <div data-bem="CheckoutFooter__cash" className={CN.boxFooter}>
                <span className={CN.codeLabel}>{t('checkout.footer.cash')}</span>
                <div className={currencySignClasses}>
                  <CurrencySign />
                </div>
                <div className={CN.input}>
                  <MoneyInput id={ELID.checkoutCash} type="text" placeholder="" value={fromAnyToNumber(cash) <= 0 ? '' : cash} onChange={handleCashChange} onBlur={handleCashBlur} autoComplete="off" />
                </div>
              </div>

              <div data-bem="CheckoutFooter__other" className={CN.boxFooter}>
                <span className={CN.boxFooterFirst}>
                  {t('checkout.footer.other')}
                  <span className={CN.tooltip}>
                    <div className={CN.tooltipCentered}>{t('checkout.footer.otherPaymentTooltip')}</div>
                  </span>
                </span>
                <div className={currencySignClasses}>
                  <CurrencySign />
                </div>
                <div className={CN.input}>
                  <MoneyInput onChange={handleOtherChange} onBlur={handleOtherBlur} type="text" placeholder="" value={fromAnyToNumber(other) <= 0 ? '' : other} autoComplete="off" />
                </div>
              </div>
            </div>
            <div data-bem="CheckoutFooter__buttons" className={CN.buttons}>
              <button data-bem="CheckoutFooter__back-button" onClick={toggleBack} className={CN.goBackToCheckout}>
                {t('checkout.footer.backButton')}
              </button>

              <button data-bem="CheckoutFooter__complete-button" onClick={handleComplete} className={CN.completeCheckout} disabled={blockComplete || busyComplete}>
                {busyComplete ? <InlineSpinner variant="light" /> : <>{t('checkout.footer.completeButton')}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// this extra export is needed for tests that provide their own mock for connect()
export { CheckoutFooter as CheckoutFooter4Test };

const CheckoutFooterHoc = connect(
  (state) => ({
    calendarModes: selectCalendarModes(state),
    checkoutOption: selectCheckoutPaymentOption(state),
  }),
  (dispatch) => bindActionCreators({ feChangePaymentOption }, dispatch),
)(CheckoutFooter);

export default CheckoutFooterHoc;
